import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Zap } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ModelSelector } from "@/components/ModelSelector";
import { cacheManager } from "@/utils/cacheUtils";

export default function StoryIdeaPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClientInstance = useQueryClient();
  
  // Get template characteristics from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const templateData = {
    template: urlParams.get('template'),
    bookType: urlParams.get('bookType'),
    targetWordCount: urlParams.get('targetWordCount'),
    genre: urlParams.get('genre'),
    tone: urlParams.get('tone'),
    style: urlParams.get('style'),
    themes: urlParams.get('themes'),
    setting: urlParams.get('setting'),
    protagonist: urlParams.get('protagonist'),
    conflict: urlParams.get('conflict'),
    pacing: urlParams.get('pacing'),
    prompt: urlParams.get('prompt')
  };
  
  const [storyIdea, setStoryIdea] = useState(templateData.prompt || "");
  const [selectedModel, setSelectedModel] = useState<any>(null);

  const generateBlueprintMutation = useMutation({
    mutationFn: async (data: { storyIdea: string }) => {
      const requestData = {
        idea: data.storyIdea,
        selectedLlmModel: selectedModel?.apiName,
        templateCharacteristics: templateData.template ? {
          bookType: templateData.bookType,
          targetWordCount: templateData.targetWordCount ? parseInt(templateData.targetWordCount) : undefined,
          genre: templateData.genre,
          tone: templateData.tone,
          style: templateData.style,
          themes: templateData.themes ? templateData.themes.split(', ') : undefined,
          setting: templateData.setting,
          protagonist: templateData.protagonist,
          conflict: templateData.conflict,
          pacing: templateData.pacing
        } : undefined
      };
      const resp = await apiRequest("/api/story-projects/generate-blueprint", "POST", requestData);
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();                         // 👈 ensure parsed JSON
    },
    onSuccess: (data: any) => {

      // ✅ CACHE THE RESPONSE DATA: Pre-populate cache with blueprint response to prevent redundant API calls
      if (data.projectId && data.data) {
        // Cache the project summary data (includes hasBlueprint: true and progress)
        const projectSummary = {
          id: data.projectId,
          uuid: data.projectId, // Assuming projectId is the UUID
          title: data.data.title || "Untitled Project",
          bookType: data.data.bookType || "NOVEL",
          targetWordCount: data.data.targetWordCount || 80000,
          hasBlueprint: true,
          progress: data.progress || {},
          idea: data.data.idea || "",
          projectData: data.data
        };

        console.log('✅ STEP 1.5: Pre-caching blueprint response data to prevent redundant API calls', {
          projectId: data.projectId,
          hasBlueprint: projectSummary.hasBlueprint,
          progressKeys: data.progress ? Object.keys(data.progress) : null
        });

        // Cache project summary
        cacheManager.set('project-summary', data.projectId, projectSummary, 5 * 60 * 1000);
        queryClientInstance.setQueryData(['story-project-summary', data.projectId], projectSummary);

        // Cache blueprint data
        if (data.data.blueprint) {
          const blueprintData = {
            exists: true,
            loaded: true,
            data: data.data.blueprint,
            error: null
          };
          cacheManager.setBlueprint(data.projectId, data.data.blueprint);
          queryClientInstance.setQueryData(['project-blueprint', data.projectId], blueprintData);
        }

        // ✅ SURGICAL FIX: Set query defaults to prevent automatic refetches for 5 minutes
        queryClientInstance.setQueryDefaults(['story-project-summary', data.projectId], {
          staleTime: 5 * 60 * 1000,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        });
        queryClientInstance.setQueryDefaults(['project-blueprint', data.projectId], {
          staleTime: 5 * 60 * 1000,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        });

      }

      toast({
        title: "Blueprint Generated!",
        description: "Your story project has been created successfully.",
      });
      // Navigate to the new project with ideaHub tab (using React Router now!)
      navigate(`/story-workspace/${data.projectId}?tab=ideaHub`);

      // Add a small delay to check if navigation completed
      setTimeout(() => {
      }, 100);
    },
    onError: (error: any) => {
      // Enhanced error handling with model selection support
      let errorMessage = error.message || "Failed to generate blueprint. Please try again.";
      
      // Check for 400 errors which often indicate model/provider issues
      if (error.message?.includes('400') || error.message?.includes('Bad Request')) {
        errorMessage = selectedModel 
          ? `The selected model (${selectedModel.modelName}) is currently overloaded or unavailable. Please try selecting a different model.`
          : "The AI service is currently overloaded. Please select a different AI model and try again.";
      }

      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive",
      });

      console.log('Blueprint generation failed:', { 
        selectedModel: selectedModel?.modelName, 
        error: error.message 
      });
    },
  });

  const handleGenerateBlueprint = async () => {
    if (!storyIdea.trim()) {
      toast({
        title: "Story Idea Required",
        description: "Please enter your story idea before generating a blueprint.",
        variant: "destructive",
      });
      return;
    }
    
    // Generate blueprint directly like the dashboard
    generateBlueprintMutation.mutate({
      storyIdea: storyIdea.trim(),
    });
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6 space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
            Start Your Masterpiece- Story Idea
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tell us about your story idea, and we'll help you craft a comprehensive blueprint including 
            book details, world-building concepts, and prepare all project tabs. This streamlined process 
            will auto-generate content for all sections with a single click.
          </p>
        </div>

        {/* Template Characteristics Display */}
        {templateData.template && (
          <Card className="max-w-2xl mx-auto bg-blue-50 border-blue-200">
            <CardHeader>
              <CardTitle className="text-lg text-blue-900 flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-100 text-blue-800">
                  Template Selected
                </Badge>
                AI will use these characteristics
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                {templateData.bookType && (
                  <div>
                    <span className="font-medium text-blue-800">Book Type:</span>
                    <span className="ml-2 text-blue-700">{templateData.bookType.replace('_', ' ')}</span>
                  </div>
                )}
                {templateData.targetWordCount && (
                  <div>
                    <span className="font-medium text-blue-800">Target Words:</span>
                    <span className="ml-2 text-blue-700">{parseInt(templateData.targetWordCount).toLocaleString()}</span>
                  </div>
                )}
                {templateData.genre && (
                  <div>
                    <span className="font-medium text-blue-800">Genre:</span>
                    <span className="ml-2 text-blue-700">{templateData.genre}</span>
                  </div>
                )}
                {templateData.tone && (
                  <div>
                    <span className="font-medium text-blue-800">Tone:</span>
                    <span className="ml-2 text-blue-700">{templateData.tone}</span>
                  </div>
                )}
              </div>
              {templateData.themes && (
                <div>
                  <span className="font-medium text-blue-800 text-sm">Themes:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {templateData.themes.split(', ').map((theme, index) => (
                      <Badge key={index} variant="secondary" className="text-xs bg-blue-100 text-blue-800">
                        {theme}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-xs text-blue-600 italic">
                These characteristics will guide the AI in creating your blueprint, chapter outlines, and story content.
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="max-w-2xl mx-auto">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-purple-600" />
              Your Story Idea
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <Textarea
              placeholder="Describe your story idea here... What's the premise? Who are the main characters? What's the setting? The more details you provide, the better blueprint we can create for you."
              value={storyIdea}
              onChange={(e) => setStoryIdea(e.target.value)}
              className="min-h-[150px] resize-none"
            />
            
            {/* Model Selection Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <h3 className="text-sm font-medium">AI Model Selection</h3>
                  <p className="text-xs text-muted-foreground">
                    Choose the AI model for blueprint generation
                    {selectedModel && ` • Current: ${selectedModel.modelName}`}
                  </p>
                </div>
                <ModelSelector
                  taskType="blueprint"
                  onModelSelect={(model) => setSelectedModel(model)}
                  baseCost={1000} // Base cost for blueprint generation
                >
                  <Button variant="outline" size="sm">
                    {selectedModel ? `${selectedModel.modelName}` : 'Select Model'}
                  </Button>
                </ModelSelector>
              </div>
              
              {!selectedModel && (
                <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                  ⚠️ No model selected. The system will use the default model, which may be overloaded.
                </div>
              )}
            </div>
            
            <Button 
              onClick={handleGenerateBlueprint}
              disabled={!storyIdea.trim() || generateBlueprintMutation.isPending}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
              size="lg"
            >
              {generateBlueprintMutation.isPending ? (
                <>
                  <Zap className="mr-2 h-4 w-4 animate-spin" />
                  Generating Blueprint...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Blueprint
                  {selectedModel && (
                    <span className="ml-2 text-xs opacity-80">
                      ({(1000 * selectedModel.creditMultiplier).toLocaleString()} credits)
                    </span>
                  )}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

      </div>
    </DashboardLayout>
  );
}